import { useEffect, useMemo, useState } from "react";

/** Shared admin search timing — Students, Batch Students, Orders, etc. */
export const SEARCH_DEBOUNCE_MS = 600;
export const SEARCH_MIN_CHARS = 3;

/** Returns trimmed search text only when it meets the minimum length; otherwise "". */
export function toActiveSearch(
  value: string,
  minChars: number = SEARCH_MIN_CHARS,
): string {
  const trimmed = value.trim();
  return trimmed.length >= minChars ? trimmed : "";
}

export function useDebounce<T>(value: T, delay: number = SEARCH_DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/**
 * Debounced search input with a minimum character gate before the value is
 * considered "active" for API calls. Reuse across list pages.
 */
export function useDebouncedSearch(
  initialValue = "",
  options?: { debounceMs?: number; minChars?: number },
) {
  const debounceMs = options?.debounceMs ?? SEARCH_DEBOUNCE_MS;
  const minChars = options?.minChars ?? SEARCH_MIN_CHARS;

  const [searchInput, setSearchInput] = useState(initialValue);
  const debouncedSearch = useDebounce(searchInput, debounceMs);
  const activeSearch = useMemo(
    () => toActiveSearch(debouncedSearch, minChars),
    [debouncedSearch, minChars],
  );
  const isSearchPending = searchInput !== debouncedSearch;

  const clearSearch = () => setSearchInput("");

  return {
    searchInput,
    setSearchInput,
    /** Raw debounced input (may be under minChars). */
    debouncedSearch,
    /** Value safe to send to the API ("" until minChars). */
    activeSearch,
    isSearchPending,
    clearSearch,
  };
}
